import React, { useState, useEffect } from "react";
import { RightOutlined, ClockCircleOutlined } from "@ant-design/icons";
import { Spin } from "antd";
import { fetchAccountSettings } from "../services/api";
import SecurityModal from "./SecurityModal";
import "../../../styles/dashboardCardTokens.css";
import "./ProfileSection.css";

function formatLastUpdated(isoString) {
  if (!isoString) return "—";
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (24 * 60 * 60 * 1000));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "1 day ago";
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)} week(s) ago`;
  return date.toLocaleDateString();
}

export default function ProfileSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [securityModalOpen, setSecurityModalOpen] = useState(false);

  useEffect(() => {
    fetchAccountSettings()
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const profile = data?.profile ?? {};
  const dealership = data?.dealership_details ?? {};
  const contact = data?.contact_information ?? {};

  const orgName = profile?.name ?? dealership?.name ?? "—";
  const initials = profile?.initials ?? (orgName !== "—" ? orgName.split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase() : "?");
  const subscriptionLevel = profile?.subscription_level ?? "—";
  const searchesUsed = profile?.searches_used ?? "—";
  const recommendedListingsGiven = profile?.recommended_listings_given ?? "—";
  const lastUpdated = formatLastUpdated(profile?.last_updated);

  const contactName = contact?.name ?? "—";
  const contactPhone = contact?.phone_number ?? dealership?.phone_number ?? "—";
  const address = dealership?.address ?? "—";
  const city = dealership?.city ?? "—";
  const state = dealership?.state ?? "—";
  const email = dealership?.email ?? "—";
  const dealershipPhone = dealership?.phone_number ?? "—";

  if (loading) {
    return (
      <div className="account-profile-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="account-profile-section">
      <div className="account-profile-grid">
        {/* Left column */}
        <div className="account-profile-col account-profile-col-left">
          <div className="account-card dashboard-card-base account-profile-card">
            <div className="account-profile-header">
              <div className="account-profile-avatar-wrap">
                <div className="account-profile-avatar">{initials}</div>
              </div>
              <div className="account-profile-info">
                <h2 className="account-profile-org-name">{orgName}</h2>
                <span className="account-profile-plan-pill">{subscriptionLevel} &gt;</span>
              </div>
            </div>
            <div className="account-profile-meta">
              <div className="account-profile-meta-row account-profile-meta-row-field">
                <span className="account-profile-meta-label">Searches Used:</span>
                <span className="account-profile-meta-value">{searchesUsed}</span>
              </div>
              <div className="account-profile-meta-row account-profile-meta-row-field">
                <span className="account-profile-meta-label">Recommended Listings Given:</span>
                <span className="account-profile-meta-value">{recommendedListingsGiven}</span>
              </div>
            </div>
            <div className="account-profile-last-updated">
              <ClockCircleOutlined />
              <span>Last updated {lastUpdated}</span>
            </div>
          </div>

          <div className="account-card dashboard-card-base">
            <h3 className="account-card-title dashboard-card-title">Contact Information</h3>
            <div className="account-personal-row">
              <span className="account-personal-label">Name</span>
              <span className="account-personal-value">{contactName}</span>
              <RightOutlined className="account-personal-arrow" />
            </div>
            <div className="account-personal-row">
              <span className="account-personal-label">Phone Number</span>
              <span className="account-personal-value">{contactPhone}</span>
              <RightOutlined className="account-personal-arrow" />
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="account-profile-col">
          <div className="account-card dashboard-card-base">
            <h3 className="account-card-title dashboard-card-title">Dealership Details</h3>
            <div className="account-overview-row">
              <span className="account-overview-label">Name</span>
              <span className="account-overview-value">{orgName}</span>
            </div>
            <div className="account-overview-row">
              <span className="account-overview-label">Address</span>
              <span className="account-overview-value">{address}</span>
            </div>
            <div className="account-overview-row">
              <span className="account-overview-label">City</span>
              <span className="account-overview-value">{city}</span>
            </div>
            <div className="account-overview-row">
              <span className="account-overview-label">State</span>
              <span className="account-overview-value">{state}</span>
            </div>
            <div className="account-overview-row">
              <span className="account-overview-label">Phone Number</span>
              <span className="account-overview-value">{dealershipPhone}</span>
            </div>
            <div className="account-overview-row">
              <span className="account-overview-label">Email</span>
              <span className="account-overview-value">{email}</span>
            </div>
          </div>

          <div className="account-card dashboard-card-base">
            <h3 className="account-card-title dashboard-card-title">Security</h3>
            <div className="account-security-row">
              <span>Password &amp; sign-in methods</span>
              <button
                type="button"
                className="account-action-btn"
                onClick={() => setSecurityModalOpen(true)}
              >
                Manage
              </button>
            </div>
          </div>
        </div>
      </div>

      <SecurityModal
        open={securityModalOpen}
        onClose={() => setSecurityModalOpen(false)}
      />
    </div>
  );
}
